import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { communityAPI } from '../services/api';

const DEFAULT_SERVICES = [
  'Employee engagement',
  'Culture & values',
  'Leadership',
  'Wellbeing',
  'DEI',
  'HR strategy',
  'Surveys & listening',
];

const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const CommunitySubmit: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [profileType, setProfileType] = useState<'company' | 'individual'>('company');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [about, setAbout] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [services, setServices] = useState<string[]>([]);

  const canSubmit = useMemo(() => {
    return Boolean(firstName.trim() && lastName.trim() && email.trim() && password && displayName.trim());
  }, [firstName, lastName, email, password, displayName]);

  const toggleService = (s: string) => {
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleLogoChange = async (file: File | null) => {
    if (!file) {
      setLogoDataUrl('');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setLogoDataUrl(dataUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      if (!canSubmit) {
        setError('Please complete the required fields.');
        return;
      }

      await communityAPI.submit({
        firstName,
        lastName,
        email,
        password,
        profileType,
        companyName,
        displayName,
        logoDataUrl,
        summary,
        about,
        services,
        location: { country, city },
        contactEmail,
        websiteUrl,
      });

      navigate('/community/submit/success', { replace: true });
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to submit profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 12, md: 14 } }}>
      <Button variant="text" onClick={() => navigate('/community')}>
        Back to Community
      </Button>

      <Typography variant="h3" component="h1" sx={{ mt: 2, fontWeight: 900 }}>
        Create your profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
        Your listing will be saved as draft and reviewed by an admin before it appears in the directory.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            select
            label="Profile type"
            value={profileType}
            onChange={(e) => setProfileType(e.target.value as any)}
            fullWidth
          >
            <MenuItem value="company">Company</MenuItem>
            <MenuItem value="individual">Individual</MenuItem>
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth required />
            <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth required />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth required />
            <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth required />
          </Stack>

          <TextField label="Company name (optional)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} fullWidth />
          <TextField label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} fullWidth required />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Logo (optional)
            </Typography>
            <Button variant="outlined" component="label" disabled={loading}>
              Upload logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
              />
            </Button>
            {logoDataUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Box component="img" src={logoDataUrl} alt="Logo preview" sx={{ width: 96, height: 96, borderRadius: 2, objectFit: 'cover' }} />
              </Box>
            )}
          </Box>

          <TextField label="Summary (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} fullWidth />
          <TextField label="About (optional)" value={about} onChange={(e) => setAbout(e.target.value)} fullWidth multiline minRows={4} />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Services
            </Typography>
            <FormGroup>
              {DEFAULT_SERVICES.map((s) => (
                <FormControlLabel
                  key={s}
                  control={<Checkbox checked={services.includes(s)} onChange={() => toggleService(s)} />}
                  label={s}
                />
              ))}
            </FormGroup>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} fullWidth />
            <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
          </Stack>

          <TextField label="Contact email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} fullWidth />
          <TextField label="Website URL" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} fullWidth />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button type="submit" variant="contained" disabled={loading || !canSubmit}>
              {loading ? 'Submitting...' : 'Submit profile'}
            </Button>
            <Button variant="outlined" disabled={loading} onClick={() => navigate('/community')}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default CommunitySubmit;
